import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Landmark, Vote, Megaphone, Trophy, Loader2, ScrollText, Users, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PreviewRibbon, DEMO } from "@/components/cef/cefShared";

/* CITY HALL (CEF) — /cef/politics/:serverId?token=<player-session>.
   Citizens run for office and vote from their phone; owners call and advance
   elections from the Owner Deck. Winners take the server's seat of government.
   No token (browser preview) → demo election so anyone can explore. */

const GOLD = "#e8b93e";
const STATUS_TXT = { registration: ["border-sky-400/50 text-sky-300", "CANDIDACY REGISTRATION OPEN"], voting: ["border-emerald-500/50 text-emerald-300", "VOTING OPEN — CAST YOUR BALLOT"], closed: ["border-white/15 text-zinc-400", "CLOSED"] };

export default function PoliticsCefPage() {
  const { serverId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const demo = !token;

  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState("");
  const [form, setForm] = useState({ name: "", slogan: "" });
  const [announce, setAnnounce] = useState("");
  const [taxes, setTaxes] = useState({ tax_sales: null, tax_income: null });

  const load = useCallback(() => {
    if (demo) { setSt(DEMO.politics); return; }
    api.get(`/nc/servers/${serverId}/politics`, { params: { token } }).then((r) => setSt(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [demo, serverId, token]);
  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  const run = async () => {
    if (demo) { toast.info("Preview mode — in-game your candidacy lands on the real ballot"); return; }
    setBusy("run");
    try {
      const r = await api.post(`/nc/servers/${serverId}/politics/candidacy`, form, { params: { token } });
      toast.success(r.data.message);
      setForm({ name: "", slogan: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  const vote = async (c) => {
    if (demo) { toast.info(`Preview mode — in-game this casts your real ballot for ${c.name}`); return; }
    setBusy(c.id);
    try {
      const r = await api.post(`/nc/servers/${serverId}/politics/vote`, { candidate_id: c.id }, { params: { token } });
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  const mayorAct = async (payload) => {
    if (demo) { toast.info("Preview: seated mayors push this live to the economy"); return; }
    setBusy("mayor");
    try {
      const r = await api.post(`/nc/servers/${serverId}/mayor/action`, payload, { params: { token } });
      toast.success(r.data.message);
      setAnnounce("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  useEffect(() => {
    if (st?.economy && taxes.tax_sales === null) setTaxes({ tax_sales: st.economy.tax_sales ?? 10, tax_income: st.economy.tax_income ?? 10 });
  }, [st, taxes.tax_sales]);

  const el = st?.election;
  const gov = st?.government;
  const govsys = st?.government_system; // workshop government packs (head title, slogans)
  const total = el?.total_votes || 0;

  return (
    <div data-testid="politics-cef" className="min-h-screen bg-[#070605] text-zinc-100 p-5 sm:p-8 flex flex-col gap-6 relative" style={{ backgroundImage: "radial-gradient(70% 50% at 50% 0%, rgba(232,185,62,0.06), transparent 70%)" }}>
      {demo && <PreviewRibbon what="city hall" />}

      {/* masthead */}
      <div className="flex items-center gap-4 flex-wrap pt-6">
        <span className="w-11 h-11 border-2 flex items-center justify-center" style={{ borderColor: GOLD }}>
          <Landmark className="w-5 h-5" style={{ color: GOLD }} />
        </span>
        <div>
          <h1 className="font-sans font-bold uppercase tracking-[0.3em] text-xl" style={{ color: GOLD }}>{govsys?.name || "City Hall"}</h1>
          <p className="font-mono text-[9px] tracking-[0.3em] text-zinc-500 uppercase">{st?.server || "…"} · {govsys ? `${govsys.council_title} · ${govsys.slogan || "civic office"}` : "elections & civic office"}</p>
        </div>
        {gov && (
          <div data-testid="politics-government" className="ml-auto border px-4 py-2.5 flex items-center gap-3" style={{ borderColor: `${GOLD}55`, background: "rgba(232,185,62,0.05)" }}>
            <Trophy className="w-4 h-4" style={{ color: GOLD }} />
            <div>
              <p className="font-mono text-[8px] tracking-[0.3em] uppercase text-zinc-500">{gov.office} in office</p>
              <p className="font-sans font-semibold text-sm" style={{ color: GOLD }}>{gov.name}</p>
            </div>
          </div>
        )}
        {!gov && st && (
          <div data-testid="office-vacant" className="ml-auto border border-red-500/40 bg-red-500/10 px-4 py-2.5 flex items-center gap-3" style={{ animation: "ncPulse 1.8s ease-in-out infinite" }}>
            <Trophy className="w-4 h-4 text-red-400" />
            <div>
              <p className="font-mono text-[8px] tracking-[0.3em] uppercase text-red-300">Office vacant</p>
              <p className="font-sans font-semibold text-sm text-red-200">Register your candidacy below</p>
            </div>
          </div>
        )}
      </div>

      {!st && <Loader2 className="w-5 h-5 animate-spin mx-auto mt-20" style={{ color: GOLD }} />}

      {/* mayor's office — live economic powers for the seated officeholder */}
      {st?.you?.is_mayor && (
        <div data-testid="mayor-office" className="border p-5 flex flex-col gap-4" style={{ borderColor: `${GOLD}66`, background: "rgba(232,185,62,0.07)" }}>
          <p className="font-mono text-[10px] tracking-[0.35em] uppercase flex items-center gap-2" style={{ color: GOLD }}><Trophy className="w-4 h-4" /> {govsys?.head_title || "Mayor"}'s office — live powers</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[["tax_sales", "City sales tax"], ["tax_income", "City income tax"]].map(([field, label]) => (
              <div key={field}>
                <div className="flex justify-between font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-400 mb-1.5"><span>{label}</span><span style={{ color: GOLD }}>{taxes[field] ?? "…"}%</span></div>
                <input data-testid={`mayor-${field}`} type="range" min="0" max="30" step="1" value={taxes[field] ?? 10}
                  onChange={(e) => setTaxes({ ...taxes, [field]: Number(e.target.value) })}
                  onMouseUp={() => mayorAct({ kind: "tax", field, value: taxes[field] })} onTouchEnd={() => mayorAct({ kind: "tax", field, value: taxes[field] })}
                  className="w-full accent-[#e8b93e]" />
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input data-testid="mayor-announce" value={announce} maxLength={200} onChange={(e) => setAnnounce(e.target.value)} placeholder="Announce a policy to the whole city…"
              className="flex-1 bg-[#050505] border border-white/15 px-3 py-2.5 font-mono text-xs text-zinc-100 focus:outline-none" />
            <button data-testid="mayor-announce-send" disabled={busy === "mayor" || announce.trim().length < 5} onClick={() => mayorAct({ kind: "announce", text: announce.trim() })}
              className="px-5 font-mono text-[10px] uppercase tracking-[0.2em] font-bold text-black disabled:opacity-40" style={{ backgroundColor: GOLD }}>
              {busy === "mayor" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Announce"}
            </button>
          </div>
          <p className="font-mono text-[8px] text-zinc-500">Tax changes hit the live economy and the city market on the next ticks.</p>
        </div>
      )}

      {/* city bulletins */}
      {st && (st.announcements || []).length > 0 && (
        <div className="border border-white/10 bg-black/40 p-5 flex flex-col gap-2">
          <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-zinc-500 flex items-center gap-1.5"><Megaphone className="w-3 h-3" /> City bulletins</p>
          {st.announcements.map((a) => (
            <div key={a.id} data-testid={`bulletin-${a.id.slice(0, 6)}`} className="border-b border-white/5 pb-2">
              <p className="font-mono text-[11px] text-zinc-200 leading-relaxed">{a.text}</p>
              <p className="font-mono text-[8px] text-zinc-500 mt-0.5">{a.author} · {String(a.at).slice(0, 10)}</p>
            </div>
          ))}
        </div>
      )}

      {st && !el && (
        <div data-testid="politics-none" className="border border-dashed border-white/15 p-10 text-center flex flex-col gap-2">
          <p className="font-mono text-xs text-zinc-400">No election is running. City Hall is quiet — ask your server owner to call one from the Owner Deck.</p>
        </div>
      )}

      {el && (
        <>
          <div data-testid="politics-status" className={`border px-4 py-3 flex items-center gap-3 flex-wrap ${STATUS_TXT[el.status]?.[0]}`} style={{ background: "rgba(255,255,255,0.02)" }}>
            <Megaphone className="w-4 h-4" />
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase">{el.office} election — {STATUS_TXT[el.status]?.[1]}</p>
            <span className="ml-auto font-mono text-[9px] text-zinc-500 flex items-center gap-1.5"><Users className="w-3 h-3" /> {total} ballots cast</span>
          </div>

          {/* candidates */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {st.candidates.length === 0 && <p className="font-mono text-[10px] text-zinc-500 col-span-full">No candidates yet — be the first name on the ballot.</p>}
            {st.candidates.map((c) => {
              const share = total ? Math.round((c.votes / total) * 100) : 0;
              const mine = st.you?.candidate_id === c.id;
              const voted = st.you?.voted_for === c.id;
              return (
                <div key={c.id} data-testid={`candidate-${c.id.slice(0, 6)}`} className={`border p-5 flex flex-col gap-3 transition-colors ${mine ? "bg-white/[0.04]" : "bg-black/40"}`} style={{ borderColor: voted ? GOLD : "rgba(255,255,255,0.1)" }}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-sans font-bold text-lg text-zinc-50">{c.name}</p>
                    {mine && <span className="font-mono text-[8px] uppercase border border-sky-400/50 text-sky-300 px-1.5 py-0.5">you</span>}
                    {voted && <span className="font-mono text-[8px] uppercase px-1.5 py-0.5 border flex items-center gap-1" style={{ borderColor: `${GOLD}66`, color: GOLD }}><CheckCircle2 className="w-3 h-3" /> your vote</span>}
                  </div>
                  <p className="font-mono text-[10px] leading-relaxed text-zinc-400 flex-1">{c.slogan || "No platform published."}</p>
                  <div>
                    <div className="flex justify-between font-mono text-[9px] text-zinc-500 mb-1"><span>{c.votes} votes</span><span>{share}%</span></div>
                    <div className="h-1.5 bg-white/10 overflow-hidden">
                      <div className="h-full transition-all duration-700" style={{ width: `${share}%`, backgroundColor: GOLD, boxShadow: `0 0 10px ${GOLD}66` }} />
                    </div>
                  </div>
                  {el.status === "voting" && !mine && (
                    <button data-testid={`vote-${c.id.slice(0, 6)}`} disabled={busy === c.id || !!st.you?.voted_for} onClick={() => vote(c)}
                      className="font-mono text-[10px] uppercase tracking-[0.25em] py-3 transition-colors disabled:opacity-40 flex items-center justify-center gap-2 text-black"
                      style={{ backgroundColor: GOLD }}>
                      {busy === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Vote className="w-4 h-4" />} Vote {c.name.split(" ")[0]}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* run for office */}
          {el.status === "registration" && !st.you?.candidate_id && (
            <div data-testid="candidacy-form" className="border border-white/10 bg-black/40 p-5 flex flex-col gap-3">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase" style={{ color: GOLD }}>Run for {el.office}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input data-testid="candidacy-name" value={form.name} maxLength={60} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={`Campaign name (default: ${st.you?.name || "your citizen name"})`}
                  className="bg-[#050505] border border-white/15 px-3 py-3 font-mono text-xs text-zinc-100 focus:outline-none focus:ring-1" style={{ "--tw-ring-color": GOLD }} />
                <input data-testid="candidacy-slogan" value={form.slogan} maxLength={200} onChange={(e) => setForm({ ...form, slogan: e.target.value })} placeholder="Your platform — one line that wins the room"
                  className="bg-[#050505] border border-white/15 px-3 py-3 font-mono text-xs text-zinc-100 focus:outline-none focus:ring-1" />
              </div>
              <button data-testid="candidacy-submit" disabled={busy === "run"} onClick={run} className="w-fit font-mono text-[10px] uppercase tracking-[0.25em] px-5 py-3 text-black transition-colors disabled:opacity-40 flex items-center gap-2" style={{ backgroundColor: GOLD }}>
                {busy === "run" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Megaphone className="w-4 h-4" />} Register candidacy
              </button>
            </div>
          )}
          {el.status === "registration" && st.you?.candidate_id && (
            <p data-testid="candidacy-registered" className="font-mono text-[10px] text-sky-300 border border-sky-400/40 bg-sky-400/5 px-4 py-3">You're on the ballot — campaign in the streets while registration is open.</p>
          )}
        </>
      )}

      {/* archive */}
      {st && (st.past || []).length > 0 && (
        <div className="border border-white/10 bg-black/40 p-5 flex flex-col gap-2">
          <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-zinc-500 flex items-center gap-1.5"><ScrollText className="w-3 h-3" /> Hall of office</p>
          {st.past.map((p) => (
            <div key={p.id} data-testid={`past-${p.id.slice(0, 6)}`} className="flex items-center gap-3 font-mono text-[10px] text-zinc-400 border-b border-white/5 pb-1.5">
              <span className="text-zinc-600">{String(p.closed_at || "").slice(0, 10)}</span>
              <span className="uppercase tracking-[0.15em]">{p.office}</span>
              <span className="font-semibold" style={{ color: GOLD }}>{(p.winner || {}).name || "No winner"}</span>
              <span className="ml-auto text-zinc-600">{p.total_votes} votes</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
